import React, { createContext, useContext, useReducer, useEffect } from 'react';

// Inlined from the (now-deleted) legacy `AthenaComponents/mockAthenaData.js`
// during the Epic 12 Story 13B file cleanup — this was the only live
// import out of that file. Kept locally so AthenaContext stays self-
// contained without re-introducing the dead-tree dependency.
const generateSessionId = () =>
  `TARA_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// Athena Action Types
const ATHENA_ACTIONS = {
  // Session Management
  START_SESSION: 'START_SESSION',
  END_SESSION: 'END_SESSION',
  UPDATE_SESSION_STEP: 'UPDATE_SESSION_STEP',
  UPDATE_SESSION_DATA: 'UPDATE_SESSION_DATA',
  
  // Analysis State
  SET_ANALYSIS_STARTED: 'SET_ANALYSIS_STARTED',
  SET_PROCESSING: 'SET_PROCESSING',
  SET_AWAITING_CONFIRMATION: 'SET_AWAITING_CONFIRMATION',
  
  // Chat & Messages
  ADD_MESSAGE: 'ADD_MESSAGE',
  CLEAR_MESSAGES: 'CLEAR_MESSAGES',
  SET_MESSAGES: 'SET_MESSAGES',
  
  // Step Components
  SHOW_STEP_COMPONENT: 'SHOW_STEP_COMPONENT',
  HIDE_STEP_COMPONENT: 'HIDE_STEP_COMPONENT',
  
  // Review & Validation
  SHOW_REVIEW_PANEL: 'SHOW_REVIEW_PANEL',
  HIDE_REVIEW_PANEL: 'HIDE_REVIEW_PANEL',
  SET_REVIEW_DATA: 'SET_REVIEW_DATA',
  ADD_EXPERT_VALIDATION: 'ADD_EXPERT_VALIDATION',
  SET_REGENERATING: 'SET_REGENERATING',
  
  // Report Generation
  SHOW_REPORT_GENERATOR: 'SHOW_REPORT_GENERATOR',
  HIDE_REPORT_GENERATOR: 'HIDE_REPORT_GENERATOR',
  
  // Analysis Data
  SET_ITEM_DEFINITION: 'SET_ITEM_DEFINITION',
  SET_ASSETS: 'SET_ASSETS',
  SET_DAMAGE_SCENARIOS: 'SET_DAMAGE_SCENARIOS',
  SET_THREATS: 'SET_THREATS',
  SET_ATTACK_PATHS: 'SET_ATTACK_PATHS',
  SET_FEASIBILITY: 'SET_FEASIBILITY',
  SET_RISK_LEVELS: 'SET_RISK_LEVELS',
  SET_REQUIREMENTS: 'SET_REQUIREMENTS'
};

// Athena Steps Configuration (ISO 21434 compliant)
const ATHENA_STEPS = [
  { id: 1, title: 'Item Definition', description: 'Define system scope and boundaries', timeEstimate: '15-30 min' },
  { id: 2, title: 'Asset Identification', description: 'Identify cybersecurity assets', timeEstimate: '20-45 min' },
  { id: 3, title: 'Damage Scenarios', description: 'Assess impact severity and CIA properties', timeEstimate: '30-60 min' },
  { id: 4, title: 'Threat Analysis', description: 'Identify threats using STRIDE methodology', timeEstimate: '45-90 min' },
  { id: 5, title: 'Attack Path Feasibility', description: 'Analyze attack feasibility using ISO/SAE 21434', timeEstimate: '60-120 min' },
  { id: 6, title: 'Risk Assessment', description: 'Evaluate risk levels and treatment decisions', timeEstimate: '30-60 min' },
  { id: 7, title: 'Cybersecurity Goals', description: 'Goals (Reduce) and Claims (Share/Retain) - ISO 21434 Clause 9.4', timeEstimate: '20-40 min' },
  { id: 8, title: 'Cybersecurity Concept', description: 'Controls and Requirements - ISO 21434 Clause 9.5', timeEstimate: '30-60 min' },
  { id: 9, title: 'Final Report', description: 'Generate comprehensive analysis summary', timeEstimate: '45-90 min' }
];

// Initial Athena State
const initialAthenaState = {
  // Session Management
  session: {
    id: null,
    step: 1,
    started: false,
    completed: false,
    startTime: null,
    endTime: null,
    itemDefinition: {},
    assets: [],
    damageScenarios: [],
    threats: [],
    attackPaths: [],
    feasibility: {},
    riskLevels: {},
    requirements: []
  },
  
  // Analysis State
  analysisStarted: false,
  isProcessing: false,
  awaitingConfirmation: false,
  
  // Chat & Messages
  messages: [],
  
  // UI State
  showStepComponent: false,
  showReviewPanel: false,
  showReportGenerator: false,
  
  // Review & Validation
  currentReviewData: null,
  isRegenerating: false,
  expertValidations: {},
  
  // Steps Configuration
  steps: ATHENA_STEPS,
  
  // Analysis History
  previousAnalyses: []
};

// Athena Reducer
const athenaReducer = (state, action) => {
  switch (action.type) {
    case ATHENA_ACTIONS.START_SESSION:
      return {
        ...state,
        session: {
          ...initialAthenaState.session,
          id: action.payload.sessionId || generateSessionId(),
          started: true,
          startTime: new Date()
        },
        analysisStarted: true,
        messages: []
      };
      
    case ATHENA_ACTIONS.END_SESSION:
      return {
        ...state,
        session: {
          ...state.session,
          completed: true,
          endTime: new Date()
        }
      };
      
    case ATHENA_ACTIONS.UPDATE_SESSION_STEP:
      return {
        ...state,
        session: {
          ...state.session,
          step: action.payload
        }
      };
      
    case ATHENA_ACTIONS.UPDATE_SESSION_DATA:
      return {
        ...state,
        session: {
          ...state.session,
          ...action.payload
        }
      };
      
    case ATHENA_ACTIONS.SET_ANALYSIS_STARTED:
      return { ...state, analysisStarted: action.payload };
      
    case ATHENA_ACTIONS.SET_PROCESSING:
      return { ...state, isProcessing: action.payload };
      
    case ATHENA_ACTIONS.SET_AWAITING_CONFIRMATION:
      return { ...state, awaitingConfirmation: action.payload };
      
    case ATHENA_ACTIONS.ADD_MESSAGE:
      return {
        ...state,
        messages: [...state.messages, { ...action.payload, id: Date.now() }]
      };
      
    case ATHENA_ACTIONS.CLEAR_MESSAGES:
      return { ...state, messages: [] };
      
    case ATHENA_ACTIONS.SET_MESSAGES:
      return { ...state, messages: action.payload };
      
    case ATHENA_ACTIONS.SHOW_STEP_COMPONENT:
      return { ...state, showStepComponent: true };
      
    case ATHENA_ACTIONS.HIDE_STEP_COMPONENT:
      return { ...state, showStepComponent: false };
      
    case ATHENA_ACTIONS.SHOW_REVIEW_PANEL:
      return { ...state, showReviewPanel: true };
      
    case ATHENA_ACTIONS.HIDE_REVIEW_PANEL:
      return { ...state, showReviewPanel: false };
      
    case ATHENA_ACTIONS.SET_REVIEW_DATA:
      return { ...state, currentReviewData: action.payload };
      
    case ATHENA_ACTIONS.ADD_EXPERT_VALIDATION:
      return {
        ...state,
        expertValidations: {
          ...state.expertValidations,
          [action.payload.key]: action.payload.value
        }
      };
      
    case ATHENA_ACTIONS.SET_REGENERATING:
      return { ...state, isRegenerating: action.payload };
      
    case ATHENA_ACTIONS.SHOW_REPORT_GENERATOR:
      return { ...state, showReportGenerator: true };
      
    case ATHENA_ACTIONS.HIDE_REPORT_GENERATOR:
      return { ...state, showReportGenerator: false };
      
    // Analysis Data Updates
    case ATHENA_ACTIONS.SET_ITEM_DEFINITION:
      return {
        ...state,
        session: { ...state.session, itemDefinition: action.payload }
      };
      
    case ATHENA_ACTIONS.SET_ASSETS:
      return {
        ...state,
        session: { ...state.session, assets: action.payload }
      };
      
    case ATHENA_ACTIONS.SET_DAMAGE_SCENARIOS:
      return {
        ...state,
        session: { ...state.session, damageScenarios: action.payload }
      };
      
    case ATHENA_ACTIONS.SET_THREATS:
      return {
        ...state,
        session: { ...state.session, threats: action.payload }
      };
      
    case ATHENA_ACTIONS.SET_ATTACK_PATHS:
      return {
        ...state,
        session: { ...state.session, attackPaths: action.payload }
      };
      
    case ATHENA_ACTIONS.SET_FEASIBILITY:
      return {
        ...state,
        session: { ...state.session, feasibility: action.payload }
      };
      
    case ATHENA_ACTIONS.SET_RISK_LEVELS:
      return {
        ...state,
        session: { ...state.session, riskLevels: action.payload }
      };
      
    case ATHENA_ACTIONS.SET_REQUIREMENTS:
      return {
        ...state,
        session: { ...state.session, requirements: action.payload }
      };
      
    default:
      return state;
  }
};

// Create Context
const AthenaContext = createContext();

// Athena Provider Component
export const AthenaProvider = ({ children }) => {
  const [state, dispatch] = useReducer(athenaReducer, initialAthenaState);

  // Action Creators
  const actions = {
    // Session Management
    startSession: (sessionData = {}) => dispatch({
      type: ATHENA_ACTIONS.START_SESSION,
      payload: sessionData
    }),
    
    endSession: () => dispatch({
      type: ATHENA_ACTIONS.END_SESSION
    }),
    
    updateStep: (step) => dispatch({
      type: ATHENA_ACTIONS.UPDATE_SESSION_STEP,
      payload: step
    }),
    
    updateSessionData: (data) => dispatch({
      type: ATHENA_ACTIONS.UPDATE_SESSION_DATA,
      payload: data
    }),
    
    // Analysis State
    setAnalysisStarted: (started) => dispatch({
      type: ATHENA_ACTIONS.SET_ANALYSIS_STARTED,
      payload: started
    }),
    
    setProcessing: (processing) => dispatch({
      type: ATHENA_ACTIONS.SET_PROCESSING,
      payload: processing
    }),
    
    setAwaitingConfirmation: (awaiting) => dispatch({
      type: ATHENA_ACTIONS.SET_AWAITING_CONFIRMATION,
      payload: awaiting
    }),
    
    // Messages
    addMessage: (message) => dispatch({
      type: ATHENA_ACTIONS.ADD_MESSAGE,
      payload: message
    }),
    
    clearMessages: () => dispatch({
      type: ATHENA_ACTIONS.CLEAR_MESSAGES
    }),
    
    // UI Control
    showStepComponent: () => dispatch({
      type: ATHENA_ACTIONS.SHOW_STEP_COMPONENT
    }),
    
    hideStepComponent: () => dispatch({
      type: ATHENA_ACTIONS.HIDE_STEP_COMPONENT
    }),
    
    showReviewPanel: () => dispatch({
      type: ATHENA_ACTIONS.SHOW_REVIEW_PANEL
    }),
    
    hideReviewPanel: () => dispatch({
      type: ATHENA_ACTIONS.HIDE_REVIEW_PANEL
    }),
    
    setReviewData: (data) => dispatch({
      type: ATHENA_ACTIONS.SET_REVIEW_DATA,
      payload: data
    }),
    
    addExpertValidation: (key, value) => dispatch({
      type: ATHENA_ACTIONS.ADD_EXPERT_VALIDATION,
      payload: { key, value }
    }),
    
    setRegenerating: (regenerating) => dispatch({
      type: ATHENA_ACTIONS.SET_REGENERATING,
      payload: regenerating
    }),
    
    showReportGenerator: () => dispatch({
      type: ATHENA_ACTIONS.SHOW_REPORT_GENERATOR
    }),
    
    hideReportGenerator: () => dispatch({
      type: ATHENA_ACTIONS.HIDE_REPORT_GENERATOR
    }),
    
    // Analysis Data
    setItemDefinition: (definition) => dispatch({
      type: ATHENA_ACTIONS.SET_ITEM_DEFINITION,
      payload: definition
    }),
    
    setAssets: (assets) => dispatch({
      type: ATHENA_ACTIONS.SET_ASSETS,
      payload: assets
    }),
    
    setDamageScenarios: (scenarios) => dispatch({
      type: ATHENA_ACTIONS.SET_DAMAGE_SCENARIOS,
      payload: scenarios
    }),
    
    setThreats: (threats) => dispatch({
      type: ATHENA_ACTIONS.SET_THREATS,
      payload: threats
    }),
    
    setAttackPaths: (paths) => dispatch({
      type: ATHENA_ACTIONS.SET_ATTACK_PATHS,
      payload: paths
    }),
    
    setFeasibility: (feasibility) => dispatch({
      type: ATHENA_ACTIONS.SET_FEASIBILITY,
      payload: feasibility
    }),
    
    setRiskLevels: (riskLevels) => dispatch({
      type: ATHENA_ACTIONS.SET_RISK_LEVELS,
      payload: riskLevels
    }),
    
    setRequirements: (requirements) => dispatch({
      type: ATHENA_ACTIONS.SET_REQUIREMENTS,
      payload: requirements
    }),

    // Helper Methods
    getCurrentStep: () => state.steps.find(step => step.id === state.session.step),
    
    getNextStep: () => state.steps.find(step => step.id === state.session.step + 1),
    
    getPreviousStep: () => state.steps.find(step => step.id === state.session.step - 1),
    
    canProgressToNextStep: () => {
      const currentStep = state.session.step;
      // Add validation logic based on current step
      switch (currentStep) {
        case 1: return Object.keys(state.session.itemDefinition).length > 0;
        case 2: return state.session.assets.length > 0;
        case 3: return state.session.damageScenarios.length > 0;
        case 4: return state.session.threats.length > 0;
        case 5: return state.session.attackPaths.length > 0;
        case 6: return Object.keys(state.session.feasibility).length > 0;
        case 7: return Object.keys(state.session.riskLevels).length > 0;
        case 8: return true; // Final step
        default: return false;
      }
    },
    
    getAnalysisProgress: () => {
      const completedSteps = state.session.step - 1;
      return Math.round((completedSteps / state.steps.length) * 100);
    }
  };

  // Auto-save session data to localStorage
  useEffect(() => {
    if (state.session.id && state.session.started) {
      const sessionData = {
        ...state.session,
        messages: state.messages,
        lastSaved: new Date()
      };
      localStorage.setItem(`athena-session-${state.session.id}`, JSON.stringify(sessionData));
    }
  }, [state.session, state.messages]);

  const value = {
    ...state,
    actions
  };

  return (
    <AthenaContext.Provider value={value}>
      {children}
    </AthenaContext.Provider>
  );
};

// Custom Hook
export const useAthena = () => {
  const context = useContext(AthenaContext);
  if (!context) {
    throw new Error('useAthena must be used within an AthenaProvider');
  }
  return context;
};

// Export action types and steps for external use
export { ATHENA_ACTIONS, ATHENA_STEPS };